import { useCallback, useEffect, useRef, useState } from 'react';
import { fontsAPI } from '../api/fonts';
import './FontManagerModal.css';

const ACCEPT = '.woff2,.woff,.ttf,.otf,.eot,font/woff2,font/woff,font/ttf,font/otf';

function deriveFamilyFromFileName(name) {
  return String(name || '')
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/[_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export default function FontManagerModal({
  open,
  websiteId,
  onClose,
  onFontsChanged,
  onOpenGoogleFonts,
  hasGoogleFonts,
}) {
  const [fonts, setFonts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [familyName, setFamilyName] = useState('');
  const fileInputRef = useRef(null);

  const loadFonts = useCallback(async () => {
    if (!websiteId) return;
    setLoading(true);
    setError('');
    try {
      const data = await fontsAPI.list(websiteId);
      setFonts(Array.isArray(data.fonts) ? data.fonts : []);
    } catch (err) {
      setError(err.message || 'Failed to load fonts');
      setFonts([]);
    } finally {
      setLoading(false);
    }
  }, [websiteId]);

  useEffect(() => {
    if (!open) return;
    setError('');
    setPendingFile(null);
    setFamilyName('');
    loadFonts();
  }, [open, loadFonts]);

  const handleFilePick = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    setPendingFile(file);
    setFamilyName((prev) => prev || deriveFamilyFromFileName(file.name));
  };

  const handleUpload = async () => {
    if (!pendingFile || !websiteId) return;
    setUploading(true);
    setError('');
    try {
      await fontsAPI.upload(websiteId, pendingFile, {
        family: familyName.trim() || undefined,
      });
      setPendingFile(null);
      setFamilyName('');
      await loadFonts();
      if (typeof onFontsChanged === 'function') onFontsChanged();
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (font) => {
    if (!font || !font.blobName) return;
    if (!window.confirm(`Remove the font "${font.family}"? Pages using it will fall back to a default font.`)) {
      return;
    }
    setError('');
    try {
      await fontsAPI.remove(websiteId, font.blobName);
      await loadFonts();
      if (typeof onFontsChanged === 'function') onFontsChanged();
    } catch (err) {
      setError(err.message || 'Failed to delete font');
    }
  };

  if (!open) return null;

  return (
    <div className="font-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="font-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="font-modal-title"
      >
        <div className="font-modal-header">
          <h3 id="font-modal-title">Custom fonts</h3>
          <button type="button" className="font-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="font-modal-toolbar">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            className="font-file-input"
            onChange={handleFilePick}
          />
          <button
            type="button"
            className="font-upload-btn font-btn-secondary"
            onClick={() => fileInputRef.current?.click()}
          >
            Choose file…
          </button>
          <input
            type="text"
            className="font-family-input"
            placeholder="Font name (shown in the font picker)"
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
          />
          <button
            type="button"
            className="font-upload-btn font-btn-primary"
            disabled={!pendingFile || uploading}
            onClick={handleUpload}
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
        <div className="font-modal-hint">
          {pendingFile ? (
            <span>Selected: <strong>{pendingFile.name}</strong></span>
          ) : (
            <span>Upload .woff2, .woff, .ttf, .otf, or .eot. Fonts become selectable everywhere a font family can be set.</span>
          )}
          {hasGoogleFonts && (
            <button
              type="button"
              className="font-google-link"
              onClick={() => {
                if (typeof onOpenGoogleFonts === 'function') onOpenGoogleFonts();
              }}
            >
              Browse Google Fonts →
            </button>
          )}
        </div>

        {error && <p className="font-modal-error">{error}</p>}

        <div className="font-modal-body">
          {loading ? (
            <p className="font-modal-status">Loading fonts…</p>
          ) : fonts.length === 0 ? (
            <p className="font-modal-status">No custom fonts yet. Upload one to get started.</p>
          ) : (
            <ul className="font-list">
              {fonts.map((font) => (
                <li key={font.blobName || font.url} className="font-list-item">
                  <div className="font-list-info">
                    <span className="font-list-name">{font.family}</span>
                    <span
                      className="font-list-preview"
                      style={{ fontFamily: `"${font.family}", sans-serif` }}
                    >
                      The quick brown fox jumps over the lazy dog
                    </span>
                    <span className="font-list-file">{font.fileName}</span>
                  </div>
                  <button
                    type="button"
                    className="font-list-delete"
                    onClick={() => handleDelete(font)}
                    title="Remove font"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="font-modal-footer">
          <button type="button" className="font-btn-secondary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
